import React from "react";
import * as Yup from "yup";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Formik, FormikHelpers } from "formik";
import { ScreenFlowFormButtons } from "./ScreenFlowFormButtons";
import { suffixes } from "../../../constants";
import { objectVals2String } from "../../../utils/objectVals2String";
import { trimOnBlur } from "../../../utils/trimOnBlur";

export interface Values {
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
}

interface Props {
  initData?: Values;
  showSuffix?: boolean;
  onComplete: (fullName: Values) => Promise<boolean>;
  onBack: () => void;
}

export const NameForm = ({
  initData,
  showSuffix = true,
  onComplete,
  onBack,
}: Props): JSX.Element => {
  // https://formik.org/docs/api/formik
  const formikInit = {
    values: {
      firstName: initData?.firstName || "",
      middleName: initData?.middleName || "",
      lastName: initData?.lastName || "",
      suffix: initData?.suffix || "",
    },
    schema: Yup.object().shape({
      firstName: Yup.string().required("First name is required"),
      middleName: Yup.string(),
      lastName: Yup.string().required("Last name is required"),
      suffix: Yup.string(),
    }),
  };

  const handleSubmit = async (
    values: Values,
    { setSubmitting, setStatus }: FormikHelpers<Values>
  ) => {
    setStatus("");
    try {
      await onComplete(values);
    } catch (error) {
      setStatus("Sorry, error: " + ((error as Error)?.message || ""));
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={formikInit.values}
      validationSchema={formikInit.schema}
      onSubmit={handleSubmit}
      key={objectVals2String(formikInit.values)}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        status,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit}>
          <Grid
            container
            spacing={1}
            sx={{
              my: 1,
            }}
          >
            <Grid item xs={12}>
              <TextField
                error={Boolean(touched.firstName && errors.firstName)}
                fullWidth
                helperText={touched.firstName && errors.firstName}
                label="First name"
                margin="normal"
                name="firstName"
                onBlur={(evt: React.FocusEvent<any>) => {
                  trimOnBlur(evt, "firstName", setFieldValue, handleBlur);
                }}
                onChange={handleChange}
                type="text"
                value={values.firstName}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                error={Boolean(touched.middleName && errors.middleName)}
                fullWidth
                helperText={touched.middleName && errors.middleName}
                label="Middle name"
                margin="normal"
                name="middleName"
                onBlur={(evt: React.FocusEvent<any>) => {
                  trimOnBlur(evt, "middleName", setFieldValue, handleBlur);
                }}
                onChange={handleChange}
                type="text"
                value={values.middleName}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                error={Boolean(touched.lastName && errors.lastName)}
                fullWidth
                helperText={touched.lastName && errors.lastName}
                label="Last name"
                margin="normal"
                name="lastName"
                onBlur={(evt: React.FocusEvent<any>) => {
                  trimOnBlur(evt, "lastName", setFieldValue, handleBlur);
                }}
                onChange={handleChange}
                type="text"
                value={values.lastName}
                variant="outlined"
              />
            </Grid>
            {showSuffix && (
              <Grid
                item
                xs={12}
                sx={{
                  mt: 2,
                }}
              >
                <FormControl fullWidth>
                  <InputLabel id="suffix-label">Suffix</InputLabel>
                  <Select
                    labelId="suffix-label"
                    id="suffix-select"
                    value={values.suffix}
                    label="Suffix"
                    name="suffix"
                    onChange={handleChange}
                  >
                    {suffixes.map((suffix: string) => (
                      <MenuItem value={suffix} key={`suffix-${suffix}`}>
                        {suffix}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <ScreenFlowFormButtons
                isSubmitting={isSubmitting}
                status={status}
                onBack={onBack}
              />
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};
